import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = 'https://api.autheniq.ai';

// Sent once the backend flips REQUIRE_API_KEY on; harmless while it is off.
// Without a key the backend returns no tenant data at all, which is what stops
// a new user or organization from seeing someone else's sessions.
const API_KEY = process.env.REACT_APP_API_KEY;
const authHeaders = API_KEY ? { 'X-API-Key': API_KEY } : {};

// The backend emits risk tiers as "Level 0".."Level 4", plus the explicit
// non-classification "Insufficient Evidence". Anything comparing against a
// tier string must go through here — reading p.risk_tier directly is what made
// the High Risk counter silently report zero.
//
// Four states, not five levels and not pass/fail. Five levels is more
// precision than the evidence supports; pass/fail throws away the graded
// response (challenge -> alert -> remove) that is the actual product. The
// state that matters most is UNKNOWN: a participant whose channels produced no
// data is unassessed, not suspicious, and must never be shown as a risk.
const STATE_UNKNOWN = 'unknown';
const STATE_VERIFIED = 'verified';
const STATE_REVIEW = 'review';
const STATE_REMOVED = 'removed';

const TIER_MAP = {
  'insufficient evidence': STATE_UNKNOWN,
  'level 0': STATE_VERIFIED,
  'level 1': STATE_VERIFIED,
  'level 2': STATE_REVIEW,
  'level 3': STATE_REVIEW,
  'level 4': STATE_REMOVED
};

export function normalizeTier(tier) {
  // No tier at all means not yet evaluated — unknown, never "verified".
  if (!tier) return STATE_UNKNOWN;
  return TIER_MAP[tier.toLowerCase().trim()] || STATE_UNKNOWN;
}

// Only Level 3+ counts as elevated. Level 2 asks for a challenge, it does not
// assert a finding.
const ELEVATED_TIERS = [STATE_REMOVED];

export const STATE_STYLE = {
  [STATE_UNKNOWN]: { label: 'UNKNOWN', color: '#64748b', dot: '⚪' },
  [STATE_VERIFIED]: { label: 'VERIFIED', color: '#22c55e', dot: '🟢' },
  [STATE_REVIEW]: { label: 'NEEDS REVIEW', color: '#f59e0b', dot: '🟡' },
  [STATE_REMOVED]: { label: 'FLAGGED', color: '#ef4444', dot: '🔴' }
};

// Coverage below this is reported as unknown regardless of the tier — it
// mirrors the engine's own refusal-to-classify threshold.
const MIN_COVERAGE = 0.5;

export function participantState(p) {
  let state = STATE_UNKNOWN;
  if (p.coverage === null || p.coverage === undefined || p.coverage >= MIN_COVERAGE) {
    state = normalizeTier(p.risk_tier);
  }
  // A passed liveness challenge verifies immediately, without waiting for the
  // end-of-meeting scoring artifact. It never overrides a worse verdict.
  if (p.challenge_passed && state === STATE_UNKNOWN) return STATE_VERIFIED;
  return state;
}

function RiskBadge({ tier, coverage, challengePassed }) {
  const state = participantState({ risk_tier: tier, coverage, challenge_passed: challengePassed });
  const style = STATE_STYLE[state];
  return (
    <span style={{ background: style.color, color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 'bold' }}>
      {style.label}
    </span>
  );
}

// Shown instead of a percentage. "56%" implies the model can tell 56 from 58;
// with most channels unmeasured it cannot, and the number reads as a
// measurement when it is mostly defaults.
function Coverage({ coverage }) {
  if (coverage === null || coverage === undefined) {
    return <span style={{ color: '#64748b', fontSize: '13px' }}>—</span>;
  }
  const low = coverage < MIN_COVERAGE;
  return (
    <span style={{ color: low ? '#f59e0b' : '#94a3b8', fontSize: '13px' }}>
      {Math.round(coverage * 100)}% signal{low ? ' (too low to score)' : ''}
    </span>
  );
}

const pct = v => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(0)}%`);
const when = v => (v ? new Date(v).toLocaleString() : '—');

const card = { background: '#1e293b', borderRadius: '12px', padding: '20px' };
const th = { padding: '8px', color: '#64748b', fontSize: '12px', textAlign: 'left' };
const td = { padding: '12px 8px' };

function StatCards({ items }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${items.length}, 1fr)`, gap: '16px', marginBottom: '24px' }}>
      {items.map(item => (
        <div key={item.label} style={card}>
          <p style={{ color: '#94a3b8', margin: '0 0 8px', fontSize: '13px' }}>{item.label}</p>
          <p style={{ fontSize: '32px', fontWeight: 'bold', margin: 0 }}>{item.value}</p>
        </div>
      ))}
    </div>
  );
}

export function SessionReport({ report, onClose }) {
  const { session, summary, participants, alerts, ai_analysis: ai } = report;

  return (
    <div id="autheniq-report" style={{ ...card, marginBottom: '24px' }}>
      <div className="report-hide" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h2 style={{ margin: 0, fontSize: '20px' }}>Session Report</h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => window.print()} style={{ padding: '8px 16px', borderRadius: '8px', background: '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
            Print / Save as PDF
          </button>
          <button onClick={onClose} style={{ padding: '8px 16px', borderRadius: '8px', background: '#334155', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
            Close
          </button>
        </div>
      </div>

      <p style={{ color: '#94a3b8', fontSize: '13px', margin: '0 0 16px' }}>
        {session.session_id} · {session.platform} · {when(session.created_at)} · {session.duration_minutes} min
        {session.ended_reason ? ` · ended (${session.ended_reason})` : ''}
      </p>

      <StatCards items={[
        { label: 'Participants', value: summary.participants },
        { label: 'Evaluations', value: summary.total_evaluations },
        { label: 'Elevated Risk', value: summary.elevated_risk_participants },
        { label: 'Alerts', value: summary.alerts_raised }
      ]} />

      {ai && (
        <div style={{ background: '#0f172a', borderRadius: '10px', padding: '16px', marginBottom: '20px', borderLeft: '3px solid #6366f1' }}>
          <p style={{ margin: '0 0 4px', fontSize: '12px', color: '#818cf8', letterSpacing: '0.05em' }}>
            AI ANALYSIS · {ai.model}
          </p>
          <p style={{ margin: '0 0 8px', fontWeight: 'bold' }}>{ai.headline}</p>
          <p style={{ margin: '0 0 12px', color: '#cbd5e1', fontSize: '14px', lineHeight: 1.6 }}>{ai.summary}</p>
          {ai.recommended_actions?.length > 0 && (
            <>
              <p style={{ margin: '0 0 4px', fontSize: '13px', color: '#94a3b8' }}>Recommended actions</p>
              <ul style={{ margin: '0 0 12px', paddingLeft: '20px', color: '#cbd5e1', fontSize: '14px' }}>
                {ai.recommended_actions.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </>
          )}
          {ai.limitations && (
            <p style={{ margin: 0, fontSize: '13px', color: '#94a3b8', fontStyle: 'italic' }}>{ai.limitations}</p>
          )}
        </div>
      )}

      <h3 style={{ fontSize: '15px', margin: '0 0 8px' }}>Participants</h3>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
        <thead>
          <tr>
            <th style={th}>PARTICIPANT</th>
            <th style={th}>ASSESSMENT</th>
            <th style={th}>COVERAGE</th>
            <th style={th}>MEASURED</th>
            <th style={th}>RAW SCORE</th>
            <th style={th}>EVALS</th>
            <th style={th}>ACTION</th>
          </tr>
        </thead>
        <tbody>
          {participants.map(p => (
            <tr key={p.participant_id} style={{ borderTop: '1px solid #334155' }}>
              <td style={td}>{p.display_name}</td>
              <td style={td}><RiskBadge tier={p.final_risk_tier} coverage={p.final_coverage} /></td>
              <td style={td}><Coverage coverage={p.final_coverage} /></td>
              <td style={{ ...td, color: '#94a3b8', fontSize: '12px' }}>
                {p.measured_channels?.length ? p.measured_channels.join(', ') : 'none'}
              </td>
              {/* The raw score stays in the report for analysts and audit,
                  out of the headline UI where it reads as a measurement. */}
              <td style={{ ...td, color: '#94a3b8', fontSize: '13px' }}>{pct(p.final_hpc_score)}</td>
              <td style={td}>{p.evaluations}</td>
              <td style={{ ...td, color: '#94a3b8', fontSize: '13px' }}>{p.final_action || '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {alerts.length > 0 && (
        <>
          <h3 style={{ fontSize: '15px', margin: '0 0 8px' }}>Alerts</h3>
          <ul style={{ margin: 0, paddingLeft: '20px', color: '#cbd5e1', fontSize: '14px' }}>
            {alerts.map(a => (
              <li key={a.id} style={{ marginBottom: '6px' }}>
                <strong>{a.risk_tier}</strong> — {a.message}
                <span style={{ color: '#64748b' }}> ({a.status}, {a.executed_action})</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function App() {
  const [allSessions, setAllSessions] = useState([]);
  const [status] = useState('Connected');
  const [meetingUrl, setMeetingUrl] = useState('');
  const [deploying, setDeploying] = useState(false);
  const [deployMsg, setDeployMsg] = useState('');
  const [report, setReport] = useState(null);
  const [reportError, setReportError] = useState('');

  useEffect(() => {
    const load = () => {
      // One request covers both views: active sessions drive the live tiles,
      // ended ones stay reachable as history so their reports don't disappear
      // the moment a meeting finishes.
      axios.get(`${API}/sessions?include_ended=true`, { headers: authHeaders })
        .then(r => setAllSessions(r.data))
        // Clear rather than keep showing stale rows if the backend stops
        // answering — old data on screen is worse than no data.
        .catch(() => setAllSessions([]));
    };
    load();
    const interval = setInterval(load, 3000);
    return () => clearInterval(interval);
  }, []);

  const sessions = allSessions.filter(s => s.status === 'active');
  const history = allSessions.filter(s => s.status !== 'active');

  // If the session a report is open for is gone entirely (tenant changed, or
  // the backend stopped answering), drop the report so nothing stale remains.
  // Ending a session must NOT close its report — that is when you want it.
  useEffect(() => {
    if (report && !allSessions.some(s => s.session_id === report.session.session_id)) {
      setReport(null);
    }
  }, [allSessions, report]);

  const viewReport = useCallback(async sessionId => {
    setReportError('');
    try {
      const r = await axios.get(`${API}/sessions/${sessionId}/report`, { headers: authHeaders });
      setReport(r.data);
    } catch (e) {
      setReport(null);
      setReportError('Could not load the report for that session.');
    }
  }, []);

  const endSession = useCallback(async sessionId => {
    try {
      await axios.post(`${API}/sessions/${sessionId}/end`, {}, { headers: authHeaders });
      // Mark it ended locally so it moves straight to history rather than
      // vanishing until the next poll.
      setAllSessions(prev => prev.map(s =>
        s.session_id === sessionId ? { ...s, status: 'ended', ended_reason: 'ended_by_host' } : s
      ));
    } catch (e) {
      setReportError('Could not end that session.');
    }
  }, []);

  const deployBot = async () => {
    if (!meetingUrl) return;
    setDeploying(true);
    setDeployMsg('');
    try {
      await axios.post(`${API}/bots/deploy`, { meeting_url: meetingUrl, bot_name: 'AuthenIQ Verify' }, { headers: authHeaders });
      setDeployMsg('Bot deployed successfully!');
      setMeetingUrl('');
    } catch (e) {
      setDeployMsg('Deploy failed — use terminal command instead.');
    }
    setDeploying(false);
  };

  const allParticipants = sessions.flatMap(s => s.participants || []);
  const participantCount = allParticipants.length;
  const countOf = state => allParticipants.filter(p => participantState(p) === state).length;
  const removedCount = allParticipants.filter(p => ELEVATED_TIERS.includes(participantState(p))).length;

  return (
    <div style={{ fontFamily: 'Inter, sans-serif', background: '#0f172a', minHeight: '100vh', color: 'white', padding: '24px' }}>
      {/* Printing targets the report only, so a saved PDF is the deliverable. */}
      <style>{`
        @media print {
          body { background: white; }
          .report-hide, .no-print { display: none !important; }
          #autheniq-report { background: white !important; color: black !important; }
          #autheniq-report * { color: black !important; }
        }
      `}</style>

      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>

        <div className="no-print" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>AuthenIQ</h1>
            <p style={{ color: '#94a3b8', margin: '4px 0 0' }}>Meeting Presence Checks</p>
          </div>
          <span style={{ background: '#22c55e22', color: '#22c55e', padding: '6px 14px', borderRadius: '20px', fontSize: '13px' }}>● {status}</span>
        </div>

        <div className="no-print" style={{ ...card, marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>Deploy Verification Bot</h3>
          <div style={{ display: 'flex', gap: '12px' }}>
            <input
              value={meetingUrl}
              onChange={e => setMeetingUrl(e.target.value)}
              placeholder="Paste meeting URL (Zoom or Google Meet)..."
              style={{ flex: 1, padding: '10px 14px', borderRadius: '8px', border: '1px solid #334155', background: '#0f172a', color: 'white', fontSize: '14px' }}
            />
            <button
              onClick={deployBot}
              disabled={deploying || !meetingUrl}
              style={{ padding: '10px 24px', borderRadius: '8px', background: deploying ? '#334155' : '#2563eb', color: 'white', border: 'none', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}
            >
              {deploying ? 'Deploying...' : 'Deploy Bot'}
            </button>
          </div>
          {deployMsg && <p style={{ margin: '8px 0 0', fontSize: '13px', color: deployMsg.includes('success') ? '#22c55e' : '#f59e0b' }}>{deployMsg}</p>}
        </div>

        <div className="no-print">
          {/* Unknown is a first-class outcome, shown with the rest rather than
              folded into a risk count — that conflation is what turned "we
              could not measure" into "suspicious". */}
          <StatCards items={[
            { label: 'Active Sessions', value: sessions.length },
            { label: 'Participants', value: participantCount },
            { label: '🟢 Verified', value: countOf(STATE_VERIFIED) },
            { label: '🟡 Needs Review', value: countOf(STATE_REVIEW) },
            { label: '🔴 Flagged', value: removedCount },
            { label: '⚪ Unknown', value: countOf(STATE_UNKNOWN) }
          ]} />
        </div>

        {reportError && <p className="no-print" style={{ color: '#f59e0b', fontSize: '13px' }}>{reportError}</p>}

        {report && <SessionReport report={report} onClose={() => setReport(null)} />}

        <div className="no-print">
          {sessions.length === 0 ? (
            <div style={{ ...card, padding: '48px', textAlign: 'center', color: '#64748b' }}>
              <p style={{ fontSize: '18px' }}>No active sessions</p>
              <p style={{ fontSize: '14px' }}>Deploy a bot to a meeting to see data here</p>
            </div>
          ) : null}

          {sessions.map(session => (
            <div key={session.session_id} style={{ ...card, marginBottom: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0, fontSize: '15px' }}>Session: {session.session_id}</h3>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => viewReport(session.session_id)} style={{ padding: '6px 14px', borderRadius: '8px', background: '#334155', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                    View report
                  </button>
                  <button onClick={() => endSession(session.session_id)} style={{ padding: '6px 14px', borderRadius: '8px', background: 'transparent', color: '#94a3b8', border: '1px solid #334155', cursor: 'pointer', fontSize: '13px' }}>
                    End session
                  </button>
                </div>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>PARTICIPANT</th>
                    <th style={th}>ASSESSMENT</th>
                    <th style={th}>SIGNAL COVERAGE</th>
                    <th style={th}>IN MEETING</th>
                  </tr>
                </thead>
                <tbody>
                  {session.participants?.map(p => (
                    <tr key={p.participant_id} style={{ borderTop: '1px solid #334155' }}>
                      <td style={td}>{p.display_name}</td>
                      <td style={td}><RiskBadge tier={p.risk_tier} coverage={p.coverage} challengePassed={p.challenge_passed} /></td>
                      <td style={td}><Coverage coverage={p.coverage} /></td>
                      <td style={td}>{p.is_active ? '🟢 Active' : '⚫ Left'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* Reports outlive the meeting. Without this list a finished session
              takes its report with it, which is exactly when you want to read it. */}
          {history.length > 0 && (
            <div style={{ ...card, marginTop: '8px' }}>
              <h3 style={{ margin: '0 0 4px', fontSize: '16px' }}>Session Reports</h3>
              <p style={{ color: '#64748b', fontSize: '13px', margin: '0 0 16px' }}>
                Completed sessions. Reports stay available after a meeting ends.
              </p>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={th}>SESSION</th>
                    <th style={th}>STARTED</th>
                    <th style={th}>PARTICIPANTS</th>
                    <th style={th}>ENDED</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(s => (
                    <tr key={s.session_id} style={{ borderTop: '1px solid #334155' }}>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: '12px' }}>
                        {s.session_id.slice(0, 8)}…
                      </td>
                      <td style={{ ...td, fontSize: '13px', color: '#94a3b8' }}>{when(s.created_at)}</td>
                      <td style={td}>{s.participants?.length || 0}</td>
                      <td style={{ ...td, fontSize: '13px', color: '#94a3b8' }}>
                        {s.ended_reason === 'inactivity_timeout' ? 'auto (inactive)' : 'by host'}
                      </td>
                      <td style={{ ...td, textAlign: 'right' }}>
                        <button onClick={() => viewReport(s.session_id)} style={{ padding: '6px 14px', borderRadius: '8px', background: '#334155', color: 'white', border: 'none', cursor: 'pointer', fontSize: '13px' }}>
                          View report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
